import { apiGet, type QueryRecord } from './http-client';

export const MY_PAYMENTS_PAGE_SIZE = 50;

type ApiPayment = {
  id: number;
  amount_cents: number;
  currency: string;
  status: string;
  provider?: string;
  reference_type?: string | null;
  reference_id?: number | null;
  payment_url?: string | null;
  created_at: string;
};

type ListBody = {
  items: ApiPayment[];
  total: number;
};

export type MyPaymentItem = {
  id: number;
  booking_id: number | null;
  amount: string;
  amount_cents: number;
  currency: string;
  status: string;
  created_at: string;
  /** Провайдер шлюза или служебное значение (например stub). */
  provider: string;
  /** Тип связанной сущности с бэкенда. */
  reference_type: string | null;
  /** Безопасная ссылка на оплату для pending-платежа (только доверенный домен шлюза). */
  payment_url: string | null;
};

function normalizeRefType(value: string | null | undefined): string | null {
  if (value == null) return null;
  const t = String(value).trim().toLowerCase();
  return t === '' ? null : t;
}

function toMyPaymentItem(p: ApiPayment): MyPaymentItem {
  const rt = normalizeRefType(p.reference_type);
  const bookingId =
    p.reference_id != null && (rt === 'booking' || rt == null) ? p.reference_id : null;
  return {
    id: p.id,
    booking_id: bookingId,
    amount: (p.amount_cents / 100).toFixed(2),
    amount_cents: p.amount_cents,
    currency: p.currency,
    status: p.status,
    created_at: p.created_at,
    provider: (p.provider ?? '').trim().toLowerCase(),
    reference_type: rt,
    payment_url: p.payment_url?.trim() || null
  };
}

const GATEWAY_PROVIDERS = new Set(['robokassa', 'yookassa', 'tochka']);

export function isGatewayPaymentProvider(provider: string): boolean {
  return GATEWAY_PROVIDERS.has(provider.trim().toLowerCase());
}

export type BillingHistoryFilter = 'all' | 'online' | 'subscription' | 'booking';

export function matchesBillingHistoryFilter(p: MyPaymentItem, f: BillingHistoryFilter): boolean {
  if (f === 'all') return true;
  if (f === 'online') return isGatewayPaymentProvider(p.provider);
  if (f === 'subscription') {
    const rt = p.reference_type ?? '';
    return rt === 'user_subscription' || rt.includes('subscription');
  }
  if (f === 'booking') return p.booking_id != null || p.reference_type === 'booking';
  return true;
}

export type MyPaymentsPage = {
  items: MyPaymentItem[];
  total: number;
};

/**
 * GET /api/v1/my-payments — только платежи текущего клиента (client-scoped).
 * Keyset: следующая страница с `before_id` = `id` последнего элемента (порядок id DESC).
 */
export function fetchMyPaymentsPage(
  accessToken: string,
  query: { before_id?: number; limit?: number } = {}
): Promise<MyPaymentsPage> {
  const q: QueryRecord = { limit: query.limit ?? MY_PAYMENTS_PAGE_SIZE };
  if (query.before_id != null && query.before_id > 0) {
    q.before_id = query.before_id;
  }
  return apiGet<ListBody>('/api/v1/my-payments', q, { accessToken }).then((body) => ({
    items: body.items.map(toMyPaymentItem),
    total: body.total
  }));
}

/** Первая страница (для обратной совместимости). */
export function listMyPayments(accessToken: string): Promise<MyPaymentItem[]> {
  return fetchMyPaymentsPage(accessToken).then((p) => p.items);
}
